import { useNavigate } from "react-router-dom";
import { Brain, GlobeHemisphereWest, LockKey, MapPin, PlugsConnected, SealCheck } from "@phosphor-icons/react";
import { EkamSiteFooter, EkamSiteHeader } from "../components/EkamSite";
import { AskEkamWidget } from "../components/AskEkam";

const PILLARS = [
  { icon: Brain, title: "Chat-first intelligence", text: "One composer for every question. Ekam detects your industry, intent and risk level, then orchestrates specialist agents with evidence and verification." },
  { icon: PlugsConnected, title: "Enterprise connector vault", text: "100+ enterprise connectors — from Salesforce and SAP to Slack and Stripe — with your credentials encrypted in a bring-your-own-key vault." },
  { icon: SealCheck, title: "Proof & governance", text: "Every run carries an evidence trail, verification status, human-review controls and a full audit log. Credits are locked before execution and settled only on success." },
  { icon: GlobeHemisphereWest, title: "Every industry, every jurisdiction", text: "From healthcare and finance to government and manufacturing — one governed workspace with role-based access and compliance-aware outputs." },
];

export default function EkamAbout() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#041718] text-slate-300" data-testid="ekam-about-page">
      <EkamSiteHeader title="About" />
      <main className="mx-auto max-w-4xl px-6 pb-10">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-cyan-300">About Ekam</p>
        <h1 className="mt-4 font-display text-4xl font-light text-white sm:text-5xl">One intelligence. Every industry.</h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400">
          Ekam is the universal AI operating system built by Anxya Tech Private Limited. It brings chat-first intelligence,
          agents, workflows, enterprise connectors and document intelligence into one governed workspace — with credit-based
          billing, role-based access and evidence-backed answers for every industry and jurisdiction.
        </p>

        <div className="mt-12 grid gap-4 sm:grid-cols-2">
          {PILLARS.map((pillar) => (
            <div key={pillar.title} className="rounded-3xl border border-teal-500/10 bg-white/[0.02] p-6" data-testid={`about-pillar-${pillar.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <pillar.icon size={26} weight="duotone" className="text-cyan-300" />
              <h2 className="mt-4 font-display text-xl text-white">{pillar.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{pillar.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-6 rounded-3xl border border-cyan-400/20 bg-cyan-400/5 p-8 sm:flex-row sm:items-center">
          <div>
            <div className="flex items-center gap-2 text-cyan-300"><MapPin size={18} /><p className="text-xs font-bold uppercase tracking-[0.22em]">Headquarters</p></div>
            <p className="mt-2 font-display text-2xl text-white" data-testid="about-hq">Pune, Maharashtra, India</p>
            <p className="mt-1 text-sm text-slate-400">Anxya Tech Private Limited · CIN: U62099PN2024PTC230490</p>
          </div>
          <button type="button" onClick={() => navigate("/ekam/contact")} data-testid="about-contact-btn" className="rounded-full bg-cyan-400 px-6 py-3 text-sm font-bold text-slate-950 transition-transform hover:scale-105">
            Talk to us
          </button>
        </div>

        <button type="button" onClick={() => window.dispatchEvent(new CustomEvent("ekam:open-assistant"))} data-testid="about-ask-ekam-btn" className="mt-6 inline-flex items-center gap-2 rounded-full border border-teal-500/15 px-5 py-2.5 text-sm text-slate-300 transition-colors hover:border-cyan-400/40 hover:text-cyan-200">
          <LockKey size={16} className="text-cyan-300" /> Have a question? Ask Ekam
        </button>
      </main>
      <EkamSiteFooter />
      <AskEkamWidget />
    </div>
  );
}
